import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["aiSearch"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _ from 'underscore';
import moment from 'moment';
import { setValue } from '@truqc/content-path';
import * as ajax from './ajax';
import { constantsRegistry } from '../constants/tableConstantsFactory';
import entityActionFactory from './factories/entityActionFactory';
import store from '../reducers/storeRegistration';
import contentChangeActionFactory from './factories/contentChangeActionFactory';
import tokenErrorActions from './tokenErrorActionCreators';
import { createAtomicPatch } from '../../behaviors/src/lib/atomic';
import { executeBulkBehaviors } from '../utils/executeBulkBehaviors';
import aiNativeBehaviorEvaluator from '../nativeBehaviors/aiNativeBehaviorEvaluator';
const entityActions = entityActionFactory('cabinet', 'forms');
const cabinetConstants = constantsRegistry('cabinet');
const contentChangeActions = contentChangeActionFactory('cabinet', 'forms');

/**
 *  Store the current cabinet identifier in state, which can be used by useEffect in the
 *  cabinet.jsx component to avoid reloads.
 */
const setCurrentCabinet = currentCabinet => ({
  type: cabinetConstants.SET_CURRENT,
  currentCabinet
});

/**
 *  Set AI request state
 */
const setAiRequest = cabinetIdentifier => ({
  type: cabinetConstants.AI_REQUEST,
  payload: {
    cabinetIdentifier
  }
});

/**
 *  Set AI success state
 */
const setAiSuccess = cabinetIdentifier => ({
  type: cabinetConstants.AI_SUCCESS,
  payload: {
    cabinetIdentifier
  }
});

/**
 *  Set AI error state
 */
const setAiErrorState = (cabinetIdentifier, error) => ({
  type: cabinetConstants.AI_ERROR,
  payload: {
    cabinetIdentifier,
    error
  }
});

/**
 *  Override entityActionFactory.serverIndex to use cabinet.<cabinet identifier> in order
 *  to independently load multiple cabinets
 */
const serverIndex = (cabinet, indexQuery) => async dispatch => {
  let cabinetIndexQuery = null;
  const cabinetStore = store.getState().cabinet;
  if (cabinetStore.currentCabinet !== cabinet) {
    dispatch(setCurrentCabinet(cabinet));
  }
  if (cabinetStore[cabinet]) {
    cabinetIndexQuery = cabinetStore[cabinet].indexQuery;
  }
  // check for expiration before sending to entityActions.serverIndex since that
  // code uses the default `cabinet` state to look for the indexQuery rather than
  // the cabinet[cabinet] state
  if (indexQuery.initialized && _.isEqual(indexQuery, cabinetIndexQuery) && moment().isBefore(moment.unix(indexQuery.expiration), 'minute')) {
    return null;
  }
  await dispatch(entityActions.serverIndex(indexQuery));
  return null;
};

/**
 *  Run behaviors on the selected items
 */
const bulkBehaviors = (cabinetIdentifier, ids, actions) => async dispatch => {
  try {
    const cabinetList = store.getState().cabinet[cabinetIdentifier].list;
    const indexQuery = store.getState().cabinet[cabinetIdentifier].indexQuery;
    await executeBulkBehaviors({
      ids,
      cabinetList,
      actions,
      cabinetIdentifier,
      filters: indexQuery.filters,
      getNativeBehaviorEvaluator: contentChangeActions.getNativeBehaviorEvaluator,
      errorHandler: err => dispatch(contentChangeActions.nativeBehaviorError('form/behaviors.evaluateSource error', err))
    });
    return null;
  } catch (e) {
    return dispatch(tokenErrorActions.handleError(e, 'form', contentChangeActions.saveAllFail.bind(this, ids, e), bulkBehaviors.bind(this, ids)));
  }
};

/**
 *  Send a delete request to the server for the selected items
 */
const bulkDelete = ids => async dispatch => {
  try {
    const response = await ajax.remove('/forms', {
      ids
    });
    return dispatch(contentChangeActions.saveAllSuccess(response.data));
  } catch (e) {
    return dispatch(tokenErrorActions.handleError(e, 'form', contentChangeActions.saveAllFail.bind(this, ids, e), bulkDelete.bind(this, ids)));
  }
};

/**
 *  Send a duplicate request to the server for the selected items
 */
const bulkDuplicate = ids => async dispatch => {
  try {
    const response = await ajax.post('/forms/duplicate', {
      ids
    });
    return dispatch(contentChangeActions.saveAllSuccess(response.data));
  } catch (e) {
    return dispatch(tokenErrorActions.handleError(e, 'form', contentChangeActions.saveAllFail.bind(this, ids, e), bulkDuplicate.bind(this, ids)));
  }
};

/**
 *  Send an undelete request to the server for the selected items
 */
const bulkUndelete = ids => async dispatch => {
  try {
    const response = await ajax.post('/forms/undelete', {
      ids
    });
    return dispatch(contentChangeActions.saveAllSuccess(response.data));
  } catch (e) {
    return dispatch(tokenErrorActions.handleError(e, 'form', contentChangeActions.saveAllFail.bind(this, ids, e), bulkUndelete.bind(this, ids)));
  }
};
const getFormatInput = (cabinetIdentifier, id) => {
  const form = store.getState().cabinet[cabinetIdentifier].list.find(f => f.id === id);
  const formType = store.getState().formType.list.find(ft => ft.identifier === form.formTypeIdentifier);
  const fi = formType.formatInput;
  return JSON.parse(fi);
};

/**
 *  Send a patch request to the server for the selected items
 */
const bulkSetContent = (cabinetIdentifier, ids, action) => async dispatch => {
  const _action$params = action.params,
    contentPath = _action$params.contentPath,
    value = _action$params.value;
  const patches = [];
  ids.forEach(id => {
    const patch = {
      id,
      patch: setValue(contentPath, {}, value)
    };
    const formatInput = getFormatInput(cabinetIdentifier, id);
    const preMergePatch = createAtomicPatch(formatInput, patch.patch);
    if (preMergePatch !== undefined && Object.keys(preMergePatch).length > 0) {
      // we are not supporting nested picker values here.
      // null/atomic setting must come before new patch.
      patches.push({
        id,
        patch: preMergePatch
      });
    }
    patches.push(patch);
  });
  try {
    const url = '/forms/applyMergePatches';
    const response = await ajax.post(url, patches);
    return dispatch(contentChangeActions.saveAllSuccess(response.data));
  } catch (e) {
    return dispatch(tokenErrorActions.handleError(e, 'form', contentChangeActions.saveAllFail.bind(this, ids, e), bulkSetContent.bind(this, ids)));
  }
};

/**
 *  Perform bulk actions on the selected items
 */
const bulkUpdate = (cabinetIdentifier, ids, actions) => async dispatch => {
  // if actions is an array, it is a set of behaviors
  if (Array.isArray(actions)) {
    // refreshing the cabinet is handled by the code that runs behaviors
    return dispatch(bulkBehaviors(cabinetIdentifier, ids, actions));
  }

  // singular actions are hardcoded and don't use behaviors. Eventually these should be
  // replaced with behaviors.
  const action = actions;
  const type = action.type;
  if (type === 'bulkSetContent') {
    await dispatch(bulkSetContent(cabinetIdentifier, ids, action));
  }
  if (type === 'bulkDelete') {
    await dispatch(bulkDelete(ids));
  }
  if (type === 'bulkDuplicate') {
    await dispatch(bulkDuplicate(ids));
  }
  if (type === 'bulkUndelete') {
    await dispatch(bulkUndelete(ids));
  }

  // refresh the cabinet view
  const currentIndexQuery = store.getState().cabinet[cabinetIdentifier].indexQuery;
  currentIndexQuery.initialized = false; // force a reload
  return store.dispatch(serverIndex(cabinetIdentifier, currentIndexQuery));
};

/**
 *  Replace the index query (saved filters, deep links). Must go through `serverIndex` so
 *  `SET_CURRENT` runs when `filters.cabinetIdentifier` differs from Redux `currentCabinet`.
 */
const serverReplaceView = view => async dispatch => {
  var _view$filters;
  const id = view === null || view === void 0 || (_view$filters = view.filters) === null || _view$filters === void 0 ? void 0 : _view$filters.cabinetIdentifier;
  if (!id) {
    return dispatch(entityActions.serverIndex(view));
  }
  return dispatch(serverIndex(id, view));
};

/**
   *  Apply the sort property to the query and call serverIndex
   */
const sortBy = (index, query) => dispatch => {
  const sortedQuery = JSON.parse(JSON.stringify(query));
  const sort = sortedQuery.sort;
  if (Array.isArray(sort) && sort.length > 0 && sort[0].index === index) {
    var _sort$0$direction, _sort$0$direction$toL;
    const currentDirection = (_sort$0$direction = sort[0].direction) === null || _sort$0$direction === void 0 || (_sort$0$direction$toL = _sort$0$direction.toLowerCase) === null || _sort$0$direction$toL === void 0 ? void 0 : _sort$0$direction$toL.call(_sort$0$direction);
    if (currentDirection === 'asc') {
      sortedQuery.sort = [{
        direction: 'desc',
        index
      }];
    } else if (currentDirection === 'desc') {
      sortedQuery.sort = [];
    } else {
      sortedQuery.sort = [{
        direction: 'asc',
        index
      }];
    }
  } else {
    sortedQuery.sort = [{
      direction: 'asc',
      index
    }];
  }
  const currentCabinet = store.getState().cabinet.currentCabinet;
  return dispatch(serverIndex(currentCabinet, sortedQuery));
};

/**
 *  Apply cabinet view style to the cabinet
 */
const setSelectedView = (cabinetIdentifier, selectedView) => ({
  type: cabinetConstants.SET_SELECTED_VIEW,
  payload: {
    cabinetIdentifier,
    selectedView
  }
});
const serverFilter = (filters, indexQuery) => async dispatch => {
  const aiSearch = filters.aiSearch,
    otherFilterData = _objectWithoutProperties(filters, _excluded);
  const cabinetIdentifier = otherFilterData.cabinetIdentifier;
  let newFilters = JSON.parse(JSON.stringify(filters));
  let newIndexQuery = JSON.parse(JSON.stringify(indexQuery));
  if (aiSearch) {
    // Dispatch AI_REQUEST to indicate AI processing has started
    dispatch(setAiRequest(cabinetIdentifier));
    try {
      // Use aiNativeBehaviorEvaluator abstraction instead of direct AI service call
      // This ensures consistent request construction, authentication, and error handling.
      // Rate limiting is enforced by the AI service.
      const aiFilters = await aiNativeBehaviorEvaluator.filters(aiSearch, cabinetIdentifier);
      newFilters = _objectSpread(_objectSpread({}, otherFilterData), aiFilters);
      newIndexQuery = _objectSpread(_objectSpread({}, indexQuery), {}, {
        filters: newFilters
      });

      // Apply the filters first
      const result = await dispatch(entityActions.serverFilter(newFilters, newIndexQuery));

      // Then dispatch AI_SUCCESS so the reducer can see the applied filters
      dispatch(setAiSuccess(cabinetIdentifier));
      return result;
    } catch (error) {
      var _error$response;
      // Set AI error in state for display in chat modal
      const errorMessage = ((_error$response = error.response) === null || _error$response === void 0 || (_error$response = _error$response.data) === null || _error$response === void 0 ? void 0 : _error$response.message) || error.message || 'An error occurred while processing your AI search request.';
      dispatch(setAiErrorState(cabinetIdentifier, errorMessage));

      // Return early to prevent applying filters with errors
      return null;
    }
  }
  return dispatch(entityActions.serverFilter(newFilters, newIndexQuery));
};
const cabinetActions = _objectSpread(_objectSpread({}, entityActions), {}, {
  bulkUpdate,
  pageNext: entityActions.pageNext,
  pagePrevious: entityActions.pagePrevious,
  serverFilter,
  serverIndex,
  serverReplaceView,
  setCurrentCabinet,
  setSelectedView,
  sortBy
});
export default cabinetActions;